import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';

import { uploadFiles } from 'data-providers/mainAnketaProvider';
import {
  getRecognizeFile,
  deleteFile,
  updateClassify,
  loadFiles,
} from 'data-providers/documentProvider';
import {
  addWaitingFile,
  checkRecognizedFiles,
  removeClassifyFile,
  removeWaitingFile,
} from 'store/fileRecognizer/actions';
import { updateCommon } from 'store/anketa';

import { PassportDocInfo } from './components/PassportDocInfo/PassportDocInfo';
import { DrivingLicenceDocInfo } from './components/DrivingLicenceDocInfo/DrivingLicenceDocInfo';

import {
  PROCESS_STATUS,
  ALL_FORMATS,
  DOCUMENT_MAX_FILE_SIZE,
  FILE_UPLOAD_MESSAGES,
  FILE_UPLOAD_INTERVAL,
  DOCUMENTS_TYPES,
} from './FileUpload.constants';
import { VOID_FUNCTION } from 'constants';
import { driverLicenseCompare, pagesCompare, prepareFormData } from './FileUpload.helpers';
import { openDocumentTrack } from 'metrika/documents';

export const useFileUploadPresenter = ({ currentPersonId, classification, doctypes }) => {
  const dragDropRef = useRef();
  const { addToast } = useToasts();
  const rootDispatch = useDispatch();

  const { waitingFiles, classifyFiles } = useSelector((state) => state.fileRecognizer);
  const { anketa } = useSelector((state) => state);
  const applicationId = anketa.application.id;
  const documents = anketa.documents;

  const [classify, setClassify] = useState(false);
  const [filesTotal, setFilesTotal] = useState(0);
  const [documentData, setDocumentData] = useState(null);
  const [documentInfo, setDocumentInfo] = useState(null);
  const [intervalCheck, setIntervalCheck] = useState(null);
  const [documentDataToDelete, setDocumentDataToDelete] = useState(null);
  const [uploadProcess, setUploadProcess] = useState(PROCESS_STATUS.INITIAL);

  // File operations
  const onLoadFiles = async (callback = VOID_FUNCTION) => {
    try {
      const filesData = await loadFiles(applicationId);

      updateFiles(filesData);
      callback();
    } catch (err) {
      console.error('Failed to load files:', err);
      addToast(FILE_UPLOAD_MESSAGES.FAILED_TO_UPLOAD_FILES, { appearance: 'error' });
    }
  };

  const updateFiles = (filesData) => {
    if (!filesData) {
      return;
    }

    rootDispatch(updateCommon({ documents: filesData }));
  };

  const setFiles = useCallback(
    (files) => {
      if (!files?.length) {
        return;
      }

      const isValidFile = (file) => {
        const isFormatValid = ALL_FORMATS.includes(file.type);
        const isSizeValid = file.size <= DOCUMENT_MAX_FILE_SIZE;

        return isFormatValid && isSizeValid;
      };

      const validFiles = files.filter(isValidFile);

      if (validFiles.length === 0) {
        addToast(FILE_UPLOAD_MESSAGES.INVALID_FILE_FORMATS, { appearance: 'error' });
        setUploadProcess(PROCESS_STATUS.INITIAL);
        setFilesTotal(0);

        return;
      }

      handleFileUpload(validFiles);
    },
    [currentPersonId, addToast]
  );

  const handleFileUpload = async (validFiles) => {
    try {
      const formData = prepareFormData(validFiles, currentPersonId);

      setUploadProcess(PROCESS_STATUS.UPLOADING);

      const uploadedFiles = await uploadFiles({ applicationId, formData });

      setUploadProcess(PROCESS_STATUS.WAITING_RECOGNITION);
      setFilesTotal(filesTotal + uploadedFiles.length);

      uploadedFiles.forEach((file) =>
        rootDispatch(addWaitingFile({ id: file.id, uuid: file.uuid }))
      );

      rootDispatch(checkRecognizedFiles());

      const timer = setInterval(async () => {
        try {
          const files = await getRecognizeFile({ id: uploadedFiles.map((file) => file.uuid) });

          if (files.length > 0) {
            files.forEach(
              ({ id, status }) =>
                status === 'PROCESSED' && rootDispatch(removeWaitingFile({ uuid: id }))
            );
          }
        } catch (err) {
          console.error('Polling file status error:', err);
          clearTimeout(timer);
          addToast(FILE_UPLOAD_MESSAGES.DOCUMENTS_UPLOAD_FAILED_RETRY, {
            appearance: 'error',
          });
          setUploadProcess(PROCESS_STATUS.INITIAL);
        }
      }, FILE_UPLOAD_INTERVAL);

      setIntervalCheck(timer);
    } catch (err) {
      console.error('Upload files failed:', err);
      setUploadProcess(PROCESS_STATUS.INITIAL);
      addToast(err.response.data.error || FILE_UPLOAD_MESSAGES.FAILED_TO_UPLOAD_FILES, {
        appearance: 'error',
      });
    }
  };

  const showFile = useCallback(
    (e) => {
      if (e) {
        e.stopPropagation();
      }

      if (!classification) {
        return;
      }

      let sortedDocuments = documents
        .filter((document) => classification.sysName === document.type.sysName)
        .sort(
          classification.sysName === DOCUMENTS_TYPES.PASSPORT
            ? pagesCompare
            : classification.sysName === DOCUMENTS_TYPES.DRIVER_LICENCE
            ? driverLicenseCompare
            : undefined
        );

      openDocumentTrack(classification.sysName);
      setDocumentData({
        type: classification,
        list: sortedDocuments,
      });
    },
    [classification, documents]
  );

  const updateType = useCallback(
    (type, ids) => {
      const targetType = doctypes.find((document) => document.sysName === type);

      return documents.map((document) =>
        ids.some((id) => id === document.legacyId) ? { ...document, type: targetType } : document
      );
    },
    [doctypes, documents]
  );

  const handleChangeType = (document, type) => {
    if (classify) {
      setClassify(false);
    }

    const filesData = updateType(type, [document.legacyId]);

    updateFiles(filesData);
    updateClassify(Number(applicationId), document.id, document.entryId, type);
  };

  // Delete operations
  const handleDeleteClick = useCallback((documentData) => {
    setDocumentDataToDelete(documentData);
  }, []);

  const handleDelete = async (documentsToDelete) => {
    setDocumentDataToDelete(null);

    await Promise.all(
      documentsToDelete.map(async (document) => {
        await deleteFile(
          Number(applicationId),
          document.id,
          document.entryId,
          classification?.sysName
        );
      })
    );

    onLoadFiles(() => {
      setUploadProcess(PROCESS_STATUS.INITIAL);
      setFilesTotal(0);
      addToast(FILE_UPLOAD_MESSAGES.DOCUMENTS_DELETION_COMPLETED, {
        appearance: 'success',
      });
    });
  };

  const handleDeleteFiles = (documents) => {
    handleDelete(documents);
  };

  const handleDeleteByType = () => {
    const filteredDocuments = documents.filter(
      (document) => classification?.sysName === document.type.sysName
    );

    handleDelete(filteredDocuments);
  };

  const agreeToDelete = useCallback(() => {
    if (documentData) {
      setDocumentData(null);
    }

    if (documentDataToDelete?.type === 'file') {
      handleDeleteFiles(documentDataToDelete.list);
    }

    if (documentDataToDelete?.type === 'type') {
      handleDeleteByType();
    }
  }, [documentData, documentDataToDelete, handleDeleteFiles, handleDeleteByType]);

  const setLeftFiles = useMemo(() => {
    return filesTotal - waitingFiles.length || 0;
  }, [filesTotal, waitingFiles]);

  useEffect(() => {
    if (classify) {
      showFile();
    }
  }, [classify]);

  useEffect(() => {
    if (waitingFiles.length === 0 && uploadProcess === PROCESS_STATUS.WAITING_RECOGNITION) {
      clearInterval(intervalCheck);

      onLoadFiles(() => {
        setUploadProcess(PROCESS_STATUS.COMPLETED);
        setFilesTotal(0);
        addToast(FILE_UPLOAD_MESSAGES.DOCUMENTS_RECOGNITION_COMPLETED, {
          appearance: 'success',
        });
      });
    }
  }, [waitingFiles]);

  useEffect(() => {
    if (uploadProcess === PROCESS_STATUS.COMPLETED && !classification) {
      setUploadProcess(PROCESS_STATUS.INITIAL);
    }
  }, [uploadProcess, classification]);

  useEffect(() => {
    if (classifyFiles && classification && classification.sysName === DOCUMENTS_TYPES.OTHER) {
      setClassify(true);
      showFile();
      rootDispatch(removeClassifyFile());
    }
  }, [classification, classifyFiles]);

  useEffect(() => {
    if (!classification) {
      return;
    }

    if (
      classification.sysName === DOCUMENTS_TYPES.PASSPORT ||
      classification.sysName === DOCUMENTS_TYPES.DRIVER_LICENCE
    ) {
      const pageList = documents.filter(
        (document) => classification.sysName === document.type.sysName
      );

      if (classification.sysName === DOCUMENTS_TYPES.PASSPORT) {
        setDocumentInfo(<PassportDocInfo pageList={pageList} classification={classification} />);
      }

      if (classification.sysName === DOCUMENTS_TYPES.DRIVER_LICENCE) {
        setDocumentInfo(
          <DrivingLicenceDocInfo pageList={pageList} classification={classification} />
        );
      }
    }

    setUploadProcess(PROCESS_STATUS.COMPLETED);
  }, [documents]);

  useEffect(() => {
    return () => {
      if (intervalCheck) {
        clearInterval(intervalCheck);
      }
    };
  }, [intervalCheck]);

  return {
    doctypes,
    classify,
    filesTotal,
    dragDropRef,
    setLeftFiles,
    documentInfo,
    documentData,
    uploadProcess,
    classification,
    documentDataToDelete,
    setFiles,
    showFile,
    setClassify,
    agreeToDelete,
    setDocumentData,
    handleChangeType,
    handleDeleteClick,
    setDocumentDataToDelete,
  };
};
